/**
 * Generic API call utilities for standardized error handling.
 *
 * Provides a consistent wrapper around fetch API calls with automatic error
 * handling, authentication header management, and standardized response parsing.
 * Includes helper functions for common HTTP methods (GET, POST, PUT, DELETE, PATCH).
 *
 * @module apiUtils
 */

import { API_BASE_URL, getAuthHeaders } from './apiConfig';
import { handleApiError } from './apiErrorHandler';
import { checkRateLimit, type RateLimitConfig } from './rateLimiter';
import { queueRequest, getQueueStatus } from './requestQueue';
import { toast } from 'sonner';
import { createCorrelationId, getClientSessionId, logger } from './logger';
import {
  notifySessionInvalidated,
  shouldInvalidateSession,
} from './sessionInvalidation';

export interface ApiCallOptions extends RequestInit {
  /** Custom error message to show to user on failure */
  errorMessage?: string;
  /** Context for logging (e.g., function name) */
  context?: string;
  /** Whether to include auth headers (default: true) */
  requireAuth?: boolean;
  /** Custom headers to merge with default headers */
  customHeaders?: Record<string, string>;
  /** Whether to parse response as JSON (default: true) */
  parseJson?: boolean;
  /** Custom return value on error (default: null) */
  errorReturnValue?: unknown;
  /** Skip prepending API_BASE_URL for static content or external URLs (default: false) */
  skipApiBase?: boolean;
  /** Custom rate limit configuration (overrides default for this call) */
  rateLimit?: RateLimitConfig;
  /** Disable rate limiting for this call (default: false) */
  disableRateLimit?: boolean;
  /** Throw handled errors instead of returning errorReturnValue (default: false) */
  throwOnError?: boolean;
  /** Response body format (default: 'json' when parseJson is true, else 'text') */
  responseType?: 'json' | 'text' | 'blob';
  /** When true, log errors but do not show toast notifications */
  suppressErrorToast?: boolean;
  /** When true, auth failures from this request do not trigger global logout */
  skipSessionInvalidation?: boolean;
}

type ApiHttpError = Error & {
  status?: number;
  logoutRequired?: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function responseErrorMessage(errorData: unknown, fallback: string): string {
  if (!isRecord(errorData)) {
    return fallback;
  }

  for (const key of ['error', 'message', 'detail', 'details']) {
    const value = errorData[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }

  return fallback;
}

function responseLogoutRequired(errorData: unknown): boolean | undefined {
  if (!isRecord(errorData)) {
    return undefined;
  }
  if (typeof errorData.logoutRequired === 'boolean') {
    return errorData.logoutRequired;
  }
  if (isRecord(errorData.details) && typeof errorData.details.logout_required === 'boolean') {
    return errorData.details.logout_required;
  }
  return undefined;
}

function getHttpStatus(error: unknown): number | undefined {
  if (isRecord(error) && typeof error.status === 'number') {
    return error.status;
  }
  return undefined;
}

/**
 * Generic API call wrapper with standardized error handling
 * 
 * This function provides a consistent way to make API calls with:
 * - Automatic error handling via handleApiError()
 * - Consistent error messages and logging
 * - Support for all HTTP methods
 * - Automatic JSON parsing
 * - Custom error return values
 * 
 * @param url - API endpoint (relative to API_BASE_URL or absolute). Use `skipApiBase: true` for static content.
 * @param options - Request options and error handling configuration
 * @param options.skipApiBase - Skip prepending API_BASE_URL (useful for static content at `/contents/`)
 * @returns Promise resolving to the response data or error return value
 * 
 * @example
 * ```typescript
 * // GET request
 * const requests = await apiCall<PartnerRequestAPI[]>(
 *   '/partner/requests',
 *   {
 *     method: 'GET',
 *     errorMessage: 'Failed to fetch partner requests',
 *     context: 'getAllPartnerRequests',
 *   }
 * );
 * 
 * @example
 * ```typescript
 * // POST request with body
 * const created = await apiCall<PartnerRequestAPI>(
 *   '/partner/requests',
 *   {
 *     method: 'POST',
 *     headers: { 'Content-Type': 'application/json' },
 *     body: JSON.stringify(requestData),
 *     errorMessage: 'Failed to create partner request',
 *     context: 'createPartnerRequest',
 *   }
 * );
 * 
 * @example
 * ```typescript
 * // Custom error return value
 * const result = await apiCall<boolean>(
 *   '/partner/requests/123',
 *   {
 *     method: 'DELETE',
 *     errorMessage: 'Failed to delete request',
 *     errorReturnValue: false, // Return false instead of null on error
 *   }
 * );
 * 
 * @example
 * ```typescript
 * // Static content (skip API_BASE_URL prepending)
 * const quiz = await apiCall<GrammarQuiz>(
 *   '/contents/grammar/A1/topic.quiz.json',
 *   {
 *     method: 'GET',
 *     skipApiBase: true, // Don't prepend /api for static content
 *     requireAuth: false,
 *     errorMessage: 'Failed to load grammar quiz',
 *   }
 * );
 * ```
 */
export async function apiCall<T = unknown>(
  url: string,
  options: ApiCallOptions = {},
): Promise<T | null> {
  const {
    errorMessage = 'An error occurred',
    context,
    requireAuth = true,
    customHeaders = {},
    parseJson = true,
    errorReturnValue = null,
    skipApiBase = false,
    rateLimit,
    disableRateLimit = false,
    throwOnError = false,
    suppressErrorToast = false,
    skipSessionInvalidation = false,
    responseType,
    ...fetchOptions
  } = options;

  const resolvedResponseType =
    responseType ?? (options.parseJson === false ? 'text' : 'json');
  const isFormDataBody = fetchOptions.body instanceof FormData;

  // Determine HTTP method for rate limiting
  const method = (fetchOptions.method || 'GET').toUpperCase();
  const correlationId = createCorrelationId(method.toLowerCase());

  // Execute the actual API call
  const executeApiCall = async (): Promise<T | null> => {
    // Construct full URL
    // Skip API_BASE_URL if skipApiBase is true (for static content) or if URL is already absolute
    let fullUrl: string;
    if (url.startsWith('http')) {
      fullUrl = url; // Absolute URL, use as-is
    } else if (skipApiBase) {
      fullUrl = url; // Static content, don't prepend API_BASE_URL
    } else {
      fullUrl = `${API_BASE_URL}${url}`; // API endpoint, prepend API_BASE_URL
    }

    // Prepare headers
    const headers: HeadersInit = {
      ...(isFormDataBody ? {} : { 'Content-Type': 'application/json' }),
      'X-Correlation-ID': correlationId,
      'X-Client-Session-ID': getClientSessionId(),
      ...(typeof window !== 'undefined'
        ? { 'X-Client-Route': `${window.location.pathname}${window.location.search}` }
        : undefined),
      ...(requireAuth ? getAuthHeaders() : undefined),
      ...customHeaders,
      ...(fetchOptions.headers || undefined),
    };

    try {
      logger.event(
        'api.request.started',
        {
          method,
          url: fullUrl,
          context,
          correlationId,
        },
        'debug',
        'API request started',
      );
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        headers,
        credentials: 'include', // Always include credentials for cookie-based auth
      });
      logger.setLastResponseContext({
        requestId: response.headers.get('X-Request-ID'),
        correlationId: response.headers.get('X-Correlation-ID') || correlationId,
      });

      // Handle non-OK responses
      if (!response.ok) {
        let errorMessageFromResponse = errorMessage;
        let logoutRequired: boolean | undefined;
        try {
          const errorData = await response.json().catch(() => null);
          errorMessageFromResponse = responseErrorMessage(errorData, errorMessage);
          logoutRequired = responseLogoutRequired(errorData);
        } catch {
          // If JSON parsing fails, use default message
        }

        // Create error object with status
        const error: ApiHttpError = new Error(errorMessageFromResponse);
        error.status = response.status;
        error.logoutRequired = logoutRequired;
        logger.event(
          'api.request.failed',
          {
            method,
            url: fullUrl,
            context,
            correlationId,
            status: response.status,
          },
          response.status >= 500 ? 'error' : 'warn',
          'API request failed',
        );
        throw error;
      }

      logger.event(
        'api.request.completed',
        {
          method,
          url: fullUrl,
          context,
          correlationId,
          status: response.status,
        },
        'debug',
        'API request completed',
      );

      // Parse response
      if (resolvedResponseType === 'blob') {
        return (await response.blob()) as T;
      }
      if (resolvedResponseType === 'text') {
        return (await response.text()) as T;
      }
      const data = await response.json();
      return data as T;
    } catch (error) {
      // Handle error using centralized error handler
      logger.error('API request threw an exception', error, {
        event: 'api.request.exception',
        method,
        url: fullUrl,
        context,
        correlationId,
      });
      const status = getHttpStatus(error);
      const logoutRequired =
        isRecord(error) && typeof error.logoutRequired === 'boolean'
          ? error.logoutRequired
          : undefined;
      const shouldEndSession =
        requireAuth &&
        !skipSessionInvalidation &&
        shouldInvalidateSession(status, logoutRequired);

      if (shouldEndSession) {
        notifySessionInvalidated({
          status,
          message: error instanceof Error ? error.message : errorMessage,
          context,
        });
      }

      handleApiError(error, errorMessage, context, {
        suppressToast: suppressErrorToast || shouldEndSession,
      });
      if (throwOnError) {
        throw error;
      }
      return errorReturnValue as T;
    }
  };

  // Apply rate limiting (skip for static content or if disabled)
  const shouldApplyRateLimit = !disableRateLimit && !skipApiBase;
  if (shouldApplyRateLimit) {
    const rateLimitCheck = checkRateLimit(method, rateLimit);

    if (!rateLimitCheck.allowed) {
      // Rate limit exceeded, try to queue the request
      try {
        const queueStatus = getQueueStatus(method);
        const queuedResult = await queueRequest(method, executeApiCall, rateLimit);

        // Only show the queued toast for user-initiated mutations, not background GETs.
        if (queueStatus.length > 0 && method !== 'GET') {
          const waitTime = rateLimitCheck.retryAfter
            ? Math.ceil(rateLimitCheck.retryAfter / 1000)
            : 1;
          const secondsText = waitTime === 1 ? 'second' : 'seconds';
          toast.info('Request queued', {
            description: `Your request is queued. Processing in ~${waitTime} ${secondsText}...`,
            duration: 3000,
          });
        }

        return queuedResult as T | null;
      } catch (queueError) {
        // Queue is full or other queue error
        const errorMessage_ =
          queueError instanceof Error
            ? queueError.message
            : 'Too many requests. Please wait before retrying.';
        toast.error('Request limit reached', {
          description: errorMessage_,
          duration: 5000,
        });

        // Create a rate limit error
        const error: Error & { status?: number; code?: string } = new Error(errorMessage_);
        error.status = 429;
        error.code = 'RATE_LIMIT_EXCEEDED';
        handleApiError(error, errorMessage, context, { suppressToast: suppressErrorToast });
        if (throwOnError) {
          throw error;
        }
        return errorReturnValue as T;
      }
    }
  }

  // Rate limit allows or rate limiting is disabled, execute immediately
  return await executeApiCall();
}

/**
 * Helper function for GET requests.
 *
 * Convenience wrapper around `apiCall` for GET requests. Automatically sets
 * the HTTP method to GET and handles error cases.
 *
 * @param url - API endpoint (relative to API_BASE_URL)
 * @param options - Additional options (errorMessage, context, etc.)
 * @returns Promise resolving to response data of type T, or null on error
 *
 * @example
 * ```typescript
 * const users = await apiGet<User[]>('/users', {
 *   errorMessage: 'Failed to fetch users',
 *   context: 'getUsers',
 * });
 * ```
 */
export async function apiGet<T = unknown>(
  url: string,
  options: Omit<ApiCallOptions, 'method'> = {},
): Promise<T | null> {
  return apiCall<T>(url, { ...options, method: 'GET' });
}

function serializeRequestBody(body: unknown): BodyInit | undefined {
  if (body == null) {
    return undefined;
  }
  if (body instanceof FormData) {
    return body;
  }
  return JSON.stringify(body);
}

/**
 * Helper function for POST requests.
 *
 * Convenience wrapper around `apiCall` for POST requests. Automatically sets
 * the HTTP method to POST and JSON stringifies the body.
 *
 * @param url - API endpoint (relative to API_BASE_URL)
 * @param body - Request body object (will be JSON stringified automatically)
 * @param options - Additional options (errorMessage, context, etc.)
 * @returns Promise resolving to response data of type T, or null on error
 *
 * @example
 * ```typescript
 * const newUser = await apiPost<User>('/users', {
 *   name: 'John',
 *   email: 'john@example.com'
 * }, {
 *   errorMessage: 'Failed to create user',
 *   context: 'createUser',
 * });
 * ```
 */
export async function apiPost<T = unknown>(
  url: string,
  body?: unknown,
  options: Omit<ApiCallOptions, 'method' | 'body'> = {},
): Promise<T | null> {
  return apiCall<T>(url, {
    ...options,
    method: 'POST',
    body: serializeRequestBody(body),
  });
}

/**
 * Helper function for PUT requests.
 *
 * Convenience wrapper around `apiCall` for PUT requests. Automatically sets
 * the HTTP method to PUT and JSON stringifies the body.
 *
 * @param url - API endpoint (relative to API_BASE_URL)
 * @param body - Request body object (will be JSON stringified automatically)
 * @param options - Additional options (errorMessage, context, etc.)
 * @returns Promise resolving to response data of type T, or null on error
 *
 * @example
 * ```typescript
 * const updated = await apiPut<User>('/users/123', {
 *   name: 'Jane',
 *   email: 'jane@example.com'
 * }, {
 *   errorMessage: 'Failed to update user',
 *   context: 'updateUser',
 * });
 * ```
 */
export async function apiPut<T = unknown>(
  url: string,
  body?: unknown,
  options: Omit<ApiCallOptions, 'method' | 'body'> = {},
): Promise<T | null> {
  return apiCall<T>(url, {
    ...options,
    method: 'PUT',
    body: serializeRequestBody(body),
  });
}

/**
 * Helper function for DELETE requests.
 *
 * Convenience wrapper around `apiCall` for DELETE requests. Automatically sets
 * the HTTP method to DELETE.
 *
 * @param url - API endpoint (relative to API_BASE_URL)
 * @param options - Additional options (errorMessage, context, errorReturnValue, etc.)
 * @returns Promise resolving to response data of type T, or null/errorReturnValue on error
 *
 * @example
 * ```typescript
 * const success = await apiDelete<boolean>('/users/123', {
 *   errorMessage: 'Failed to delete user',
 *   context: 'deleteUser',
 *   errorReturnValue: false,
 * });
 * ```
 */
export async function apiDelete<T = unknown>(
  url: string,
  options: Omit<ApiCallOptions, 'method'> = {},
): Promise<T | null> {
  return apiCall<T>(url, { ...options, method: 'DELETE' });
}

/**
 * Helper function for PATCH requests.
 *
 * Convenience wrapper around `apiCall` for PATCH requests. Automatically sets
 * the HTTP method to PATCH and JSON stringifies the body.
 *
 * @param url - API endpoint (relative to API_BASE_URL)
 * @param body - Request body object (will be JSON stringified automatically)
 * @param options - Additional options (errorMessage, context, etc.)
 * @returns Promise resolving to response data of type T, or null on error
 *
 * @example
 * ```typescript
 * const updated = await apiPatch<User>('/users/123', {
 *   email: 'newemail@example.com'
 * }, {
 *   errorMessage: 'Failed to update user email',
 *   context: 'updateUserEmail',
 * });
 * ```
 */
export async function apiPatch<T = unknown>(
  url: string,
  body?: unknown,
  options: Omit<ApiCallOptions, 'method' | 'body'> = {},
): Promise<T | null> {
  return apiCall<T>(url, {
    ...options,
    method: 'PATCH',
    body: serializeRequestBody(body),
  });
}

/**
 * Helper function for GET requests that return binary data (Blob).
 */
export async function apiBlob(
  url: string,
  options: Omit<ApiCallOptions, 'method' | 'responseType' | 'parseJson'> = {},
): Promise<Blob | null> {
  return apiCall<Blob>(url, {
    ...options,
    method: 'GET',
    responseType: 'blob',
  });
}
